import { useEffect, useState } from "react";

import { v4, replace, resolvePromiseWithDelay } from "@smart/itops-utils-basic";

import {
  getLocalSmokeballAppContext,
  observeChange,
  publishChange,
  setLocalSmokeballAppContext,
} from "./utils";
import {
  Contact,
  CreateContactRequest,
  LocalSmokeballAppContext,
  UpdateContactRequest,
  UseLocalApiOptions,
} from "../types";
import { validateTrustContact } from "../utils";

export const useContacts = ({ observe }: UseLocalApiOptions) => {
  const [currentContacts, setCurrentContacts] = useState<Contact[]>();
  const shouldObserve = observe?.includes("contacts");

  useEffect(() => {
    if (shouldObserve) {
      /**
       * We could return all the contacts from the local context directly but
       * to mimic live behaviour, we get contacts based on the roles.
       */
      const findContactsForMatter = () => {
        const localContext = getLocalSmokeballAppContext();
        const contactIds = localContext?.roles?.flatMap(({ contactId }) => {
          const contact = localContext?.contacts.find(
            (c) => c.id === contactId,
          );
          const relatedContactIds = (contact?.groupOfPeople?.people || [
            ...(contact?.company?.staff ?? contact?.company?.staffIds ?? []),
            ...(contact?.company?.directors ??
              contact?.company?.directorIds ??
              []),
            ...(contact?.trust?.contacts ?? contact?.trust?.contactIds ?? []),
          ]) as string[];
          return relatedContactIds.length > 0
            ? [...relatedContactIds, contactId]
            : contactId;
        });
        return localContext?.contacts.filter((contact) =>
          contactIds?.includes(contact.id),
        );
      };
      setCurrentContacts(findContactsForMatter());
      observeChange("contacts", () =>
        setCurrentContacts(findContactsForMatter()),
      );
    }
  }, [shouldObserve]);

  return {
    get: (contactId: string) =>
      resolvePromiseWithDelay(
        getLocalSmokeballAppContext()?.contacts?.find(
          (contact) => contact.id === contactId,
        ) as Contact,
      ),
    create: ({ person, company, trust }: CreateContactRequest) => {
      validateTrustContact(trust);

      const localContext = getLocalSmokeballAppContext();
      const createdContact = {
        id: v4(),
        versionId: v4(),
        lastUpdated: new Date().getTime(),
        isDeleted: false,
        person,
        company,
        trust: trust && Object.keys(trust).length > 0 ? trust : undefined,
      } as Contact;
      const updatedContext = {
        ...localContext,
        contacts: [...(localContext?.contacts || []), createdContact],
      } as LocalSmokeballAppContext;
      setLocalSmokeballAppContext(updatedContext);
      publishChange("contacts");

      return resolvePromiseWithDelay(createdContact);
    },
    update: ({ id, person, company, trust }: UpdateContactRequest) => {
      validateTrustContact(trust);

      const localContext = getLocalSmokeballAppContext();
      const targetContact = localContext?.contacts.find(
        (contact) => contact.id === id,
      );
      const updatedContact = {
        ...targetContact,
        person: targetContact?.person
          ? {
              ...targetContact.person,
              ...person,
            }
          : targetContact?.person,
        company: targetContact?.company
          ? {
              ...targetContact.company,
              ...company,
            }
          : targetContact?.company,
        trust:
          targetContact?.trust && Object.keys(targetContact.trust).length > 0
            ? {
                ...targetContact.trust,
                ...trust,
              }
            : undefined,
      } as Contact;
      const updatedContext = {
        ...localContext,
        contacts:
          localContext?.contacts &&
          replace(localContext.contacts, (c) => c.id === id, updatedContact),
      } as LocalSmokeballAppContext;
      setLocalSmokeballAppContext(updatedContext);
      publishChange("contacts");

      return resolvePromiseWithDelay(updatedContact as Contact);
    },
    // eslint-disable-next-line no-alert
    open: (id: string) => alert(`Opening contact window for id: ${id}`),
    current: currentContacts,
  };
};
